'use client';

import {
  AudioPlayer,
  AudioPlayerControlBar,
  AudioPlayerDurationDisplay,
  AudioPlayerElement,
  AudioPlayerMuteButton,
  AudioPlayerPlayButton,
  AudioPlayerSeekBackwardButton,
  AudioPlayerSeekForwardButton,
  AudioPlayerTimeDisplay,
  AudioPlayerTimeRange,
  AudioPlayerVolumeRange,
} from '@/components/ai-elements/audio-player';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import {
  ArrowSquareOut,
  CaretDown,
  DownloadSimple,
  File as FileIcon,
  FilmSlate,
  ImageSquare,
  MusicNote,
} from '@phosphor-icons/react';

export type MediaKind = 'image' | 'video' | 'audio' | 'file';

export interface MediaItem {
  kind: MediaKind;
  url: string;
  title?: string;
  subtitle?: string;
  mimeType?: string;
  /** Clip length in seconds, when the tool output carries one (e.g. narration tracks). */
  durationSec?: number;
  /** Poster/thumbnail still for a VIDEO item, harvested from sibling
   *  poster/thumbnail fields on the tool output. Flows through the Share
   *  button into shared_artifacts.payload.posterUrl → the /x/:slug og:video
   *  poster. Only meaningful (and only ever set) for `kind === 'video'`. */
  posterUrl?: string;
}

interface MediaArtifactRendererProps {
  input?: Record<string, unknown>;
  output?: unknown;
  className?: string;
  displayContext?: 'inline' | 'artifact';
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function asString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function inferKind(url: string, explicit?: unknown, mimeType?: unknown): MediaKind {
  const type = asString(explicit)?.toLowerCase();
  const mime = asString(mimeType)?.toLowerCase();
  if (type === 'image' || mime?.startsWith('image/')) return 'image';
  if (type === 'video' || mime?.startsWith('video/')) return 'video';
  if (type === 'audio' || type === 'music' || mime?.startsWith('audio/')) return 'audio';
  if (/\.(png|jpe?g|webp|gif|avif|svg)(\?|#|$)/i.test(url) || url.startsWith('data:image/')) return 'image';
  if (/\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url) || url.startsWith('data:video/')) return 'video';
  if (/\.(mp3|wav|m4a|ogg|flac|aac)(\?|#|$)/i.test(url) || url.startsWith('data:audio/')) return 'audio';
  return 'file';
}

function collectMediaItems(output: unknown, input?: Record<string, unknown>): MediaItem[] {
  const items = new Map<string, MediaItem>();

  const add = (url: unknown, options: Omit<MediaItem, 'url' | 'kind'> & { kind?: MediaKind; explicitKind?: unknown } = {}) => {
    const resolvedUrl = asString(url);
    if (!resolvedUrl) return;
    const kind = options.kind ?? inferKind(resolvedUrl, options.explicitKind, options.mimeType);
    // A poster still is only meaningful for a video — never let a harvested
    // poster leak onto an image/audio/file item (they preview themselves).
    const normalized = kind === 'video' ? options : { ...options, posterUrl: undefined };
    const current = items.get(resolvedUrl);
    if (current) {
      items.set(resolvedUrl, { ...current, ...normalized, kind, url: resolvedUrl });
      return;
    }
    items.set(resolvedUrl, { ...normalized, kind, url: resolvedUrl });
  };

  const visit = (value: unknown, fallbackTitle?: string) => {
    const record = asRecord(value);
    if (!record) return;

    const title = asString(record.title)
      ?? asString(record.label)
      ?? asString(record.name)
      ?? fallbackTitle;
    const subtitle = asString(record.prompt)
      ?? asString(record.description)
      ?? asString(record.narration);
    const mimeType = asString(record.mimeType) ?? asString(record.contentType);
    const durationSec = typeof record.durationSec === 'number' ? record.durationSec
      : typeof record.duration === 'number' ? record.duration
      : undefined;
    // A poster/thumbnail still that previews a VIDEO sibling on the same
    // record — tool outputs use varied field names. `add` keeps it only on
    // video items (an image item is its own preview).
    const posterUrl = asString(record.posterUrl)
      ?? asString(record.thumbnailUrl)
      ?? asString(record.coverImage)
      ?? asString(record.poster)
      ?? asString(record.previewUrl);

    add(record.audioUrl, { title: title ?? 'Audio', subtitle, mimeType, durationSec, explicitKind: 'audio' });
    add(record.videoUrl, { title: title ?? 'Video', subtitle, mimeType, durationSec, posterUrl, explicitKind: 'video' });
    add(record.imageUrl, { title: title ?? 'Image', subtitle, mimeType, explicitKind: 'image' });
    add(record.thumbnailUrl, { title: title ?? 'Thumbnail', subtitle, mimeType, explicitKind: 'image' });
    add(record.outputUrl, { title, subtitle, mimeType, durationSec, posterUrl, explicitKind: record.type });
    add(record.fileUrl, { title, subtitle, mimeType, durationSec, posterUrl, explicitKind: record.type });

    if (Array.isArray(record.images)) {
      record.images.forEach((url, index) => add(url, {
        title: title ?? `Image ${index + 1}`,
        subtitle,
        explicitKind: 'image',
      }));
    }

    for (const key of ['tracks', 'sfx', 'assets', 'results', 'videos', 'media', 'partial']) {
      const nested = record[key];
      if (Array.isArray(nested)) {
        nested.forEach((item, index) => visit(item, `${title ?? key} ${index + 1}`));
      }
    }

    if (record.asset) visit(record.asset, title);
    if (record.data) visit(record.data, title);
  };

  visit(output);
  if (items.size === 0) {
    visit(input);
  }

  return Array.from(items.values());
}

/**
 * The single most share-worthy media item in a tool result, or null. Used by
 * the artifact panel's Share button to snapshot a raw tool-output clip (which
 * has no visualization row) into a shared_artifacts link. Preference order:
 * video → image → audio → file (a video is the headline artifact; a poster/
 * thumbnail image should never win over the video it previews).
 */
export function extractPrimaryMediaItem(output: unknown, input?: Record<string, unknown>): MediaItem | null {
  const items = collectMediaItems(output, input);
  if (items.length === 0) return null;
  const byKind = (kind: MediaKind) => items.find((item) => item.kind === kind);
  return byKind('video') ?? byKind('image') ?? byKind('audio') ?? byKind('file') ?? items[0];
}

function formatClipDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function itemIcon(kind: MediaKind) {
  switch (kind) {
    case 'image':
      return <ImageSquare weight="duotone" className="size-4" />;
    case 'video':
      return <FilmSlate weight="duotone" className="size-4" />;
    case 'audio':
      return <MusicNote weight="duotone" className="size-4" />;
    default:
      return <FileIcon weight="duotone" className="size-4" />;
  }
}

function MediaActions({ item }: { item: MediaItem }) {
  return (
    <div className="flex items-center gap-1">
      <Button asChild size="icon" variant="ghost" className="size-7" title="Open in new tab">
        <a href={item.url} target="_blank" rel="noreferrer">
          <ArrowSquareOut weight="bold" className="size-4" />
        </a>
      </Button>
      <Button asChild size="icon" variant="ghost" className="size-7" title="Download">
        <a href={item.url} download>
          <DownloadSimple weight="bold" className="size-4" />
        </a>
      </Button>
    </div>
  );
}

function MediaHeader({ item }: { item: MediaItem }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3 border-b border-border bg-muted/30 px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span className="grid size-7 shrink-0 place-items-center rounded-md border border-border bg-background text-muted-foreground">
          {itemIcon(item.kind)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{item.title ?? item.kind}</p>
          {item.subtitle && (
            <p className="truncate text-xs text-muted-foreground">{item.subtitle}</p>
          )}
        </div>
      </div>
      <MediaActions item={item} />
    </div>
  );
}

function MediaCard({ item, fill }: { item: MediaItem; fill?: boolean }) {
  return (
    <article className={cn('flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background', fill && 'h-full')}>
      <MediaHeader item={item} />
      <div className={cn('min-h-0 flex-1 bg-black/95', item.kind === 'audio' && 'bg-muted/20 p-3')}>
        {item.kind === 'image' && (
          <img
            src={item.url}
            alt={item.title ?? 'Generated image'}
            className={cn('h-auto w-full object-contain', fill ? 'max-h-full' : 'max-h-[520px]')}
          />
        )}
        {item.kind === 'video' && (
          <video
            src={item.url}
            controls
            playsInline
            className={cn('w-full bg-black', fill ? 'h-full object-contain' : 'max-h-[560px]')}
          />
        )}
        {item.kind === 'audio' && (
          <AudioPlayer className="w-full">
            <AudioPlayerElement src={item.url} />
            <AudioPlayerControlBar>
              <AudioPlayerPlayButton />
              <AudioPlayerSeekBackwardButton />
              <AudioPlayerSeekForwardButton />
              <AudioPlayerTimeDisplay />
              <AudioPlayerTimeRange />
              <AudioPlayerDurationDisplay />
              <AudioPlayerMuteButton />
              <AudioPlayerVolumeRange />
            </AudioPlayerControlBar>
          </AudioPlayer>
        )}
        {item.kind === 'file' && (
          <div className="flex min-h-44 items-center justify-center bg-muted/20 p-6 text-center">
            <div>
              <FileIcon weight="duotone" className="mx-auto size-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium">{item.title ?? 'File ready'}</p>
              <p className="mt-1 text-xs text-muted-foreground">Open or download this file from the controls above.</p>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * Compact multi-clip card — replaces N stacked full MediaCards (each with its
 * own header + player) with ONE card: a header, then a row per clip
 * (icon · name · duration · caret), and a single expanded player at a time.
 * This is what a generateNarrationPlan call with several tracks was
 * rendering as before — "six sibling audio cards" reading as terminal-log
 * noise (CHAT_UI_DESIGN_REVIEW.md §4 item 8, the rendered-tool equivalent of
 * the timeline rollup).
 */
function MediaClipStack({ items }: { items: MediaItem[] }) {
  const [openUrl, setOpenUrl] = useState<string | null>(null);

  return (
    <article className="overflow-hidden rounded-lg border bg-background">
      <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
        <span className="grid size-7 shrink-0 place-items-center rounded-md border border-border bg-background text-muted-foreground">
          <MusicNote weight="duotone" className="size-4" />
        </span>
        <p className="min-w-0 flex-1 truncate text-sm font-medium">
          {items.length} clip{items.length === 1 ? '' : 's'}
        </p>
      </div>
      <ul className="divide-y divide-border/60">
        {items.map((item, index) => {
          const isOpen = openUrl === item.url;
          return (
            <li key={`${item.kind}:${item.url}`}>
              <button
                type="button"
                onClick={() => setOpenUrl((current) => (current === item.url ? null : item.url))}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <span className="text-muted-foreground">{itemIcon(item.kind)}</span>
                <span className="min-w-0 flex-1 truncate">{item.title ?? `Clip ${index + 1}`}</span>
                {typeof item.durationSec === 'number' && (
                  <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground/70">
                    {formatClipDuration(item.durationSec)}
                  </span>
                )}
                <CaretDown
                  weight="bold"
                  aria-hidden="true"
                  className={cn('size-3 shrink-0 text-muted-foreground/45 transition-transform', isOpen && 'rotate-180')}
                />
              </button>
              {isOpen && (
                <div className="border-t border-border/40 bg-muted/10 px-3 py-2.5">
                  <AudioPlayer className="w-full">
                    <AudioPlayerElement src={item.url} />
                    <AudioPlayerControlBar>
                      <AudioPlayerPlayButton />
                      <AudioPlayerSeekBackwardButton />
                      <AudioPlayerSeekForwardButton />
                      <AudioPlayerTimeDisplay />
                      <AudioPlayerTimeRange />
                      <AudioPlayerDurationDisplay />
                      <AudioPlayerMuteButton />
                      <AudioPlayerVolumeRange />
                    </AudioPlayerControlBar>
                  </AudioPlayer>
                  <div className="mt-1.5 flex justify-end">
                    <MediaActions item={item} />
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </article>
  );
}

export function MediaArtifactRenderer({
  input,
  output,
  className,
  displayContext = 'inline',
}: MediaArtifactRendererProps) {
  const items = collectMediaItems(output, input);
  if (items.length === 0) return null;

  if (displayContext === 'artifact') {
    return (
      <div className={cn('h-full min-h-0 w-full overflow-auto p-4', className)}>
        <div className={cn('grid min-h-0 gap-3', items.length === 1 ? 'h-full grid-cols-1' : 'grid-cols-1 xl:grid-cols-2')}>
          {items.map((item) => (
            <MediaCard key={`${item.kind}:${item.url}`} item={item} fill={items.length === 1} />
          ))}
        </div>
      </div>
    );
  }

  // Runs of 2+ audio clips (e.g. a narration plan's per-scene tracks) collapse
  // into ONE compact stack instead of N full-height player cards; any other
  // media in the same call (a thumbnail, a video) still renders as its own
  // card. A single voiceover/music clip is already minimal — leave it alone.
  const audioItems = items.filter((item) => item.kind === 'audio');
  const otherItems = items.filter((item) => item.kind !== 'audio');
  const shouldStack = audioItems.length > 1;

  return (
    <div className={cn('space-y-3', className)}>
      {otherItems.map((item) => (
        <MediaCard key={`${item.kind}:${item.url}`} item={item} />
      ))}
      {shouldStack ? (
        <MediaClipStack items={audioItems} />
      ) : (
        audioItems.map((item) => <MediaCard key={`${item.kind}:${item.url}`} item={item} />)
      )}
    </div>
  );
}
